import { useState, useEffect, useCallback } from "react";

interface Slide {
    image: string;
    title: string;
    description: string;
}

const slides: Slide[] = [
    {
        image: "/screenshots/slide-1.png",
        title: "See your math as you type it",
        description:
            "Snippet-style input, but instead of staring at raw LaTeX, you see the actual formatted math appear live — like a WYSIWYG editor for real math.",
    },
    {
        image: "/screenshots/slide-2.png",
        title: "Write real papers, not just equations",
        description:
            "Sections, theorems, proofs, numbered equations, table of contents — everything you need for notes, problem sets, and thesis writing.",
    },
    {
        image: "/screenshots/slide-3.png",
        title: "Upload a PDF, get editable LaTeX",
        description:
            "Scan any PDF and it converts to editable LaTeX. If something breaks on compile, AI tries to fix it automatically.",
    },
    {
        image: "/screenshots/slide-4.png",
        title: "Upload a LaTeX project, edit visually",
        description:
            "Open an existing .tex file or full LaTeX folder and get an editable visual version — no more mentally parsing nested expressions.",
    },
];

const INTERVAL_MS = 5000;

export function FeatureShowcase() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    const goTo = useCallback((i: number) => setActiveIndex(i), []);
    const next = useCallback(
        () => setActiveIndex((i) => (i + 1) % slides.length),
        [],
    );

    useEffect(() => {
        if (paused) return;
        const id = setInterval(next, INTERVAL_MS);
        return () => clearInterval(id);
    }, [paused, next]);

    return (
        <div
            className="showcase"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="showcase-inner">
                <div className="showcase-header">
                    <span className="showcase-logo">∫</span>
                    <h2 className="showcase-title">SeeTex</h2>
                    <p className="showcase-tagline">
                        The speed of Gilles Castel-style LaTeX snippets, but you see the
                        formatted math live as you type — no more staring at raw source.
                    </p>
                </div>

                <div className="showcase-slides">
                    {slides.map((slide, i) => (
                        <div
                            key={i}
                            className={`showcase-slide ${i === activeIndex ? "showcase-slide--active" : ""}`}
                        >
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="showcase-img"
                                loading={i === 0 ? "eager" : "lazy"}
                                draggable={false}
                            />
                        </div>
                    ))}
                </div>

                <div className="showcase-caption">
                    <h3 className="showcase-caption-title">
                        {slides[activeIndex].title}
                    </h3>
                    <p className="showcase-caption-desc">
                        {slides[activeIndex].description}
                    </p>
                </div>

                <div className="showcase-dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            className={`showcase-dot ${i === activeIndex ? "showcase-dot--active" : ""}`}
                            onClick={() => goTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                        />
                    ))}
                </div>

                <div className="showcase-features">
                    <span className="showcase-chip">Live WYSIWYG Math</span>
                    <span className="showcase-chip">PDF Scan → LaTeX</span>
                    <span className="showcase-chip">Tab Snippets</span>
                    <span className="showcase-chip">One-Click PDF</span>
                    <span className="showcase-chip">Cloud Sync</span>
                </div>
            </div>
        </div>
    );
}
